import * as React from "react"
import {
  useRevokeInvitationMutation,
  RevokeInvitationMutation,
} from "@modules/organization/mutations.generated"

import {
  useShowSuccessToast,
  StyledModal,
  StyledModalHeader,
  StyledModalBody,
  StyledModalActions,
  Button,
  Text,
} from "gatsby-interface"
import { ModalMessage } from "@modules/modal"
import {
  manageContributors as text,
  ui as uiText,
} from "@modules/locales/default.js"
import { MutationUpdaterFn } from "apollo-client"
import { useTracker, SegmentEventType } from "@modules/analytics"
import { commonMessageTags, FormattedMessage } from "@modules/locales"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import { StyledForm } from "@modules/ui/components/Primitives"
import {
  WorkspaceInvitationsDocument,
  WorkspaceInvitationsQuery,
} from "@modules/organization/contributors/queries.generated"

const updateOrgContributorsCacheOnRevoke = ({
  resourceId,
  contributorId,
}: {
  resourceId: string
  contributorId: string
}): MutationUpdaterFn<RevokeInvitationMutation> => {
  return cache => {
    const queryInfo = {
      query: WorkspaceInvitationsDocument,
      variables: {
        workspaceId: resourceId,
      },
    }

    const invitationsFromCache = cache.readQuery<WorkspaceInvitationsQuery>(
      queryInfo
    )

    const updatedInvitations = invitationsFromCache?.workspaceInvitations?.members?.filter(
      ({ id }) => id !== contributorId
    )

    cache.writeQuery({
      ...queryInfo,
      data: {
        workspaceInvitations: { members: updatedInvitations },
      },
    })
  }
}

export type RevokeMemberInviteModalProps = {
  closeModal: () => void
  email: string
  contributorId: string
  resourceId: string
}

function RevokeMemberInviteModal({
  closeModal,
  email,
  contributorId,
  resourceId,
}: RevokeMemberInviteModalProps) {
  const { trackSegment } = useTracker()
  const showSuccessToast = useShowSuccessToast()
  const [mutate, { loading }] = useRevokeInvitationMutation()
  const [setError, errorAlert] = useTriggerErrorAlert()

  return (
    <StyledModal variant="WARNING">
      <StyledModalHeader onCloseButtonClick={closeModal}>
        {text.headers.revokeInviteQuestion}
      </StyledModalHeader>
      <StyledModalBody>
        <ModalMessage>
          <Text>
            <FormattedMessage<"email", "strong">
              message={text.messages.revokeOrgInviteConf}
              values={{ email }}
              tags={commonMessageTags}
            />
          </Text>
          {errorAlert}
        </ModalMessage>
        <StyledForm
          onSubmit={e => {
            e.preventDefault()
            trackSegment({
              type: SegmentEventType.Track,
              event: `Revoked a member invite`,
              properties: {
                location: `Members tab - "Revoke invite" modal`,
              },
            })
            mutate({
              variables: {
                id: contributorId,
              },
              update: updateOrgContributorsCacheOnRevoke({
                resourceId,
                contributorId,
              }),
            })
              .then(() => {
                closeModal()
                showSuccessToast(text.messages.memberInvitationRevoked)
              })
              .catch(err => {
                setError(err)
              })
          }}
        >
          <StyledModalActions>
            <Button
              variant="SECONDARY"
              tone="NEUTRAL"
              type="button"
              onClick={() => {
                trackSegment({
                  type: SegmentEventType.Track,
                  event: `Cancelled revoking member invite`,
                  properties: {
                    location: `Members tab - "Revoke invite" modal`,
                  },
                })
                closeModal()
              }}
            >
              {uiText.actions.cancel}
            </Button>
            <Button
              loading={loading}
              variant="PRIMARY"
              tone="DANGER"
              type="submit"
              data-cy="revoke-invite-cta"
            >
              {text.actions.revokeInvite}
            </Button>
          </StyledModalActions>
        </StyledForm>
      </StyledModalBody>
    </StyledModal>
  )
}

export default RevokeMemberInviteModal
